import React from 'react';
import {
  Descriptions,
  Select,
} from 'antd';
import PropTypes from 'prop-types';

const { Option } = Select;

// wages/billing rates should be manually mapped because some of these columns cannot be mapped together (HARBOUR-9340)
const HEADER_ALIASES = {
  costcode: ['costcode', 'code', 'cost code'],
  name: ['name'],
  description: ['description'],
  category: ['category'],
};

const HEADER_FIELDS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'code', label: 'Cost Code', required: true },
  { key: 'description', label: 'Description', required: true },
  { key: 'category', label: 'Category', required: false },
  { key: 'hourlyWage', label: 'Hourly Wage', required: false },
  { key: 'dailyWage', label: 'Daily Wage', required: false },
  { key: 'wageAdjustment', label: 'Wage Adjustment', required: false },
  { key: 'wageMultiplier', label: 'Wage Multiplier', required: false },
  { key: 'hourlyBillingRate', label: 'Hourly Billing Rate', required: false },
  { key: 'dailyBillingRate', label: 'Daily Billing Rate', required: false },
];

const matchHeader = (key) => {
  const rowkey = key.toLowerCase().trim();
  const entry = Object.entries(HEADER_ALIASES).find(([, aliases]) => aliases.includes(rowkey));
  return entry ? entry[0] : null;
};

const renderLabel = ({ label, required }) => (
  <>
    {required && (
      <span
        style={{
          color: 'red',
          marginRight: 12,
          position: 'relative',
          top: -2,
        }}
      >
        *
      </span>
    )}
    {label}
  </>
);

export default class CostCodeHeaders extends React.Component {
  constructor(props) {
    super(props);
    this.values = {};
    this.constructHeaderMap();
    this.props.onSelect(this.values);
  }

  onSelect(type, value) {
    this.values[type] = value;
    this.props.onSelect(this.values);
  }

  constructHeaderMap() {
    const keys = Object.keys(this.props.headers);
    const { customFieldTemplate } = this.props;
    const matchedHeaders = {};
    const leftOvers = [];
    keys.forEach((key) => {
      const header = matchHeader(key);
      if (header) {
        matchedHeaders[header] = key;
      } else {
        leftOvers.push(key);
      }
    });

    this.values = {
      name: matchedHeaders.name || leftOvers.pop(),
      code: matchedHeaders.costcode || leftOvers.pop(),
      description: matchedHeaders.description || leftOvers.pop(),
      category: matchedHeaders.category,
      hourlyWage: matchedHeaders.hourlyWage,
      dailyWage: matchedHeaders.dailyWage,
      wageAdjustment: matchedHeaders.wageAdjustment,
      wageMultiplier: matchedHeaders.wageMultiplier,
      hourlyBillingRate: matchedHeaders.hourlyBillingRate,
      dailyBillingRate: matchedHeaders.dailyBillingRate,
    };

    // Auto-match custom text fields by exact title
    customFieldTemplate?.sections?.forEach((section) => {
      section.fields?.forEach((field) => {
        if (field.type !== 'text') return;
        const fieldKey = `custom_${field.id}`;
        const titleMatch = keys.find((key) => key.trim() === field.title.trim());
        this.values[fieldKey] = titleMatch;
      });
    });
  }

  render() {
    const keys = Object.keys(this.props.headers);
    const { customFieldTemplate } = this.props;

    const headerOptions = keys.map((header) => (
      <Option value={header} key={header}>{header}</Option>
    ));
    const fields = [
      ...HEADER_FIELDS,
      ...(customFieldTemplate?.sections?.flatMap((section) => (
        section.fields
          ?.filter((field) => field.type === 'text')
          .map((field) => ({
            key: `custom_${field.id}`,
            label: field.title,
          }))
      )) ?? []),
    ];

    return (
      <Descriptions title="Select Column Headers" column={3}>
        {fields.map(({ key, label, required }) => (
          <Descriptions.Item label={renderLabel({label, required})} key={key}>
            <Select
              defaultValue={this.values[key]}
              style={{ width: 150 }}
              onChange={(value) => this.onSelect(key, value)}
              allowClear
              placeholder="Select column"
            >
              {headerOptions}
            </Select>
          </Descriptions.Item>
        ))}
      </Descriptions>
    );
  }
}

CostCodeHeaders.propTypes = {
  headers: PropTypes.shape({}).isRequired,
  onSelect: PropTypes.func.isRequired,
  customFieldTemplate: PropTypes.shape({
    sections: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      fields: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        sectionId: PropTypes.string,
        title: PropTypes.string,
        type: PropTypes.string,
      })),
    })),
  }),
};

CostCodeHeaders.defaultProps = {
  customFieldTemplate: null,
};
